import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { formatMoney, formatDate } from '../utils/format';
import StatusBadge from '../components/StatusBadge';
import PageHeader from '../components/PageHeader';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/stats')
      .then((res) => setStats(res.data))
      .catch(() => setError('Failed to load dashboard data'));
  }, []);

  if (error) return <div className="error-banner">{error}</div>;
  if (!stats) return <div className="page-loading">Loading…</div>;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="A live overview of auction activity, bidding and payments." />

      <div className="stat-grid">
        <div className="stat-card accent-blue">
          <div className="stat-icon">🏷️</div>
          <div className="stat-body">
            <div className="stat-label">Auctions</div>
            <div className="stat-value">{stats.auctions.total}</div>
            <div className="stat-sub">{stats.auctions.active} active · {stats.auctions.ended} ended</div>
          </div>
        </div>
        <div className="stat-card accent-purple">
          <div className="stat-icon">🔨</div>
          <div className="stat-body">
            <div className="stat-label">Bids</div>
            <div className="stat-value">{stats.bids.total}</div>
            <div className="stat-sub">{stats.bids.active} active</div>
          </div>
        </div>
        <div className="stat-card accent-amber">
          <div className="stat-icon">💳</div>
          <div className="stat-body">
            <div className="stat-label">Transactions</div>
            <div className="stat-value">{stats.transactions.total}</div>
            <div className="stat-sub">{stats.transactions.completed} completed · {stats.transactions.pending} pending</div>
          </div>
        </div>
        <div className="stat-card accent-green">
          <div className="stat-icon">📈</div>
          <div className="stat-body">
            <div className="stat-label">Completed Volume</div>
            <div className="stat-value">{formatMoney(stats.transactions.completedAmount)}</div>
            <div className="stat-sub">{stats.users.total} registered users</div>
          </div>
        </div>
      </div>

      <div className="panel-grid">
        <div className="panel">
          <div className="panel-header">
            <h2>Recent Bids</h2>
            <Link to="/bids">View all</Link>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Auction</th><th>Bidder</th><th>Amount</th><th>When</th></tr>
            </thead>
            <tbody>
              {stats.recentBids.length === 0 && (
                <tr><td colSpan="4" className="empty-cell">No bids yet</td></tr>
              )}
              {stats.recentBids.map((b) => (
                <tr key={b.id}>
                  <td>{b.auction_title}</td>
                  <td>{b.username}</td>
                  <td>{formatMoney(b.bid_amount)}</td>
                  <td>{formatDate(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <div className="panel-header">
            <h2>Recent Transactions</h2>
            <Link to="/transactions">View all</Link>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>User</th><th>Type</th><th>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {stats.recentTransactions.length === 0 && (
                <tr><td colSpan="4" className="empty-cell">No transactions yet</td></tr>
              )}
              {stats.recentTransactions.map((t) => (
                <tr key={t.id}>
                  <td>{t.username}</td>
                  <td>{t.transaction_type}</td>
                  <td>{formatMoney(t.amount)}</td>
                  <td><StatusBadge value={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
